import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Injectable } from '@angular/core';
import { MonoTypeOperatorFunction, Observable, OperatorFunction } from 'rxjs';
import { filter, map, shareReplay, tap } from 'rxjs/operators';
import { PlatformType } from '../../common';

export const screenBreakpoints = {
  XS: 767,
  SM: 1023,
  MD: 1365,
  LG: 1920,
};

export enum Screen {
  XS = '(max-width: 767px)',
  SM = '(min-width: 768px) and (max-width: 1023px)',
  MD = '(min-width: 1024px) and (max-width: 1365px)',
  LG = '(min-width: 1366px) and (max-width: 1919px)',
  XL = '(min-width: 1920px)',
}

@Injectable({
  providedIn: 'root',
})
export class ScreenService {
  public activeScreen: Screen;
  public changed$: Observable<Screen>;
  public isXS$: Observable<boolean>;
  public isSM$: Observable<boolean>;
  public isMD$: Observable<boolean>;
  public isLG$: Observable<boolean>;
  public isXL$: Observable<boolean>;

  public constructor(private readonly breakpointObserver: BreakpointObserver) {
    const breakpoints = Object.keys(Screen).map((key: string): any => Screen[key as 'XS' | 'SM' | 'MD' | 'LG' | 'XL']);
    this.changed$ = this.breakpointObserver.observe(breakpoints).pipe(
      this.filterExactlyOneBreakpointMatch(),
      this.mapMatchingBreakpointToScreen(),
      tap((screen: Screen): void => {
        this.activeScreen = screen;
      }),
      shareReplay({
        refCount: true,
        bufferSize: 1,
      })
    );

    this.isXS$ = this.changed$.pipe(
      map((screen: Screen): boolean => Screen.XS === screen),
      shareReplay({
        refCount: true,
        bufferSize: 1,
      })
    );

    this.isSM$ = this.changed$.pipe(
      map((screen: Screen): boolean => Screen.SM === screen),
      shareReplay({
        refCount: true,
        bufferSize: 1,
      })
    );

    this.isMD$ = this.changed$.pipe(
      map((screen: Screen): boolean => Screen.MD === screen),
      shareReplay({
        refCount: true,
        bufferSize: 1,
      })
    );

    this.isLG$ = this.changed$.pipe(
      map((screen: Screen): boolean => Screen.LG === screen),
      shareReplay({
        refCount: true,
        bufferSize: 1,
      })
    );

    this.isXL$ = this.changed$.pipe(
      map((screen: Screen): boolean => Screen.XL === screen),
      shareReplay({
        refCount: true,
        bufferSize: 1,
      })
    );
  }

  private filterExactlyOneBreakpointMatch(): MonoTypeOperatorFunction<BreakpointState> {
    return filter((breakpointState: BreakpointState): boolean => {
      const { breakpoints } = breakpointState;
      const length = Object.values(breakpoints).filter((isMatch: boolean): boolean => isMatch).length;
      return length === 1;
    });
  }

  private mapMatchingBreakpointToScreen(): OperatorFunction<BreakpointState, Screen> {
    return map((breakpointState: BreakpointState): Screen => {
      const breakPoints = Object.entries(breakpointState.breakpoints);
      const matchingBreakpoint = breakPoints.find(([breakpoint, isMatch]: [string, boolean]): boolean => isMatch);
      const [screen] = matchingBreakpoint;
      return screen as Screen;
    });
  }

  public mapScreenToPlatformType = (screen: Screen): PlatformType => {
    switch (screen) {
      case Screen.XS:
        return PlatformType.Mobile;
      case Screen.SM:
        return PlatformType.Tablet;
      case Screen.MD:
        return PlatformType.Web;
      case Screen.XL:
        return PlatformType.Web;
      default:
        return PlatformType.Web;
    }
  };
}
